import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import CustomerHeader from "../../shared/CustomerHeader";
import { BuilderNavProvider } from "../../components/builder/BuilderNavContext";
import BuilderProgress from "../../components/builder/BuilderProgress";
import { classNames } from "../../helpers/classNames";
import { useEffect } from "react";
import { isSafeStepRouteName } from "../../util/safeStepRouteName";
import { withBookTag } from "../../util/bookTag";
import { requestedBookId } from "../../util/navigationUtils";
import { readLocalCartBookIds } from "../../services/shopifyCartService";
import { hasPendingPdfOffer } from "../../pages/front/account/PdfOffer";
// Registers how kept changes are re-sent (book edits, page order).
import "../../services/trackedSaves";
import { flushSavedQueue } from "../../util/saveTracker";

// Builder steps already rebuilt on the redesign: they lay themselves out
// (WizardShell) and get the stepper in the header. The rest keep the old
// wrapper and their own inline progress until their step lands.
const REBUILT_ROUTES = ["/create-book", "/create", "/cover-image", "/review", "/finalize-order", "/pdf-offer", "/gift/welcome", "/gift/plan", "/gift/books", "/gift/done"];

const stepToRouteMap = {
  'Create_New_Book': '/create-book',
  'Upload_Your_Cover_Photo': '/dashboard',
  'Upload_Images': '/create',
  'Design_Your_Cover_Page': '/cover-image',
  'Review_Your_Book': '/review',
  'Place_Order': '/review'
};

const UserLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { myBooks, booksLoading, data: Profile } = useSelector((state) => state.profile);
  const { coverImages } = useSelector((state) => state.sketch);
  const { activeBookId } = useSelector(state => state.stepFlow)
  const isCheckoutPage = location.pathname.includes("checkout");
  const searchParams = new URLSearchParams(location.search);
  const currentStepCode = searchParams.get('stepCode');
  // Preserved across every redirect below: the tag is not on the book until the
  // customer names it, so losing it here downgrades a PDF buyer to physical.
  const currentTag = searchParams.get('tag');

  const hasPlacedOrder = Profile?.bookData?.some(book => book.isOrderPlaces === true);
  // With 2+ books, a plain /dashboard is the My Books grid: redirecting to one book would leave
  // the others (e.g. a finished book never added to the cart) unreachable. A link naming a book
  // (?bookId=, the recovery emails) still goes straight to that book.
  const hasMultipleBooks = myBooks?.length > 1;

  // Changes an earlier visit couldn't save (closed tab, no connection) are kept
  // on the device; send them once the customer is signed in again.
  const signedIn = !!Profile?._id;
  useEffect(() => {
    if (signedIn) flushSavedQueue();
  }, [signedIn]);

  useEffect(() => {
    if (!booksLoading) {
      // 1. If no books exist, redirect to create a new book. Not on the gift
      // pages: a gift recipient starts with no books, and the gift page is
      // where they choose how to make them.
      if (!myBooks.length && !location.pathname.includes("create-book") && !location.pathname.startsWith("/gift/")) {
        navigate(withBookTag("/create-book?stepCode=Create_New_Book", currentTag));
        return;
      }

      // 2. If user has books but has NOT placed an order, and is on /dashboard (without a specific step),
      // redirect them to continue their book at the correct route — unless they have several books
      // and no specific one was asked for, in which case the dashboard shows them all. Not while the
      // PDF downsell link waits for this sign-in: UserRoute is taking them to /pdf-offer.
      if (
        Profile?.bookData?.length > 0 &&
        !hasPlacedOrder &&
        location.pathname === "/dashboard" &&
        !currentStepCode &&
        (!hasMultipleBooks || !!requestedBookId(myBooks)) &&
        !hasPendingPdfOffer()
      ) {
        // Use activeBookId if available, otherwise fallback to the last book in the list
        // Try to find in Profile.bookData first (has latestStepCode object)
        const activeBookFromProfile = Profile.bookData.find(book => book._id === activeBookId);
        
        // If not in Profile summary, fallback to last unordered book
        const lastBook = Profile.bookData.filter(book => !book.isOrderPlaces).pop() || Profile.bookData[Profile.bookData.length - 1]; 
        
        const targetBook = activeBookFromProfile || (activeBookId ? null : lastBook);

        if (!targetBook) return;

        // Already in the cart: they finished it and chose to order it — resume at Finalize Order.
        if (readLocalCartBookIds().has(String(targetBook._id))) {
          navigate("/finalize-order");
          return;
        }

        // Smart step selection: only move to nextStepCode if latestStepCode is already completed
        const latest = targetBook.latestStepCode;
        const next = targetBook.nextStepCode;
        const stepObj = (typeof latest === 'object' && latest?.isCompleted === 'yes' && next) ? next : latest;
        
        const targetStep = typeof stepObj === 'object' ? stepObj?.code : stepObj;
        
        // Use routeName from DB only if allowlisted; otherwise fallback to local map
        let targetRoute =
          typeof stepObj === "object" &&
          stepObj?.routeName &&
          isSafeStepRouteName(stepObj.routeName)
            ? stepObj.routeName
            : stepToRouteMap[targetStep] || "/dashboard";
        let targetStepCode = targetStep;

        // Special case: if on Place_Order step, go to review
        if (targetStep === 'Place_Order') {
          targetRoute = '/review';
          targetStepCode = 'Review_Your_Book';
        }

        navigate(withBookTag(`${targetRoute}?stepCode=${targetStepCode || "Upload_Your_Cover_Photo"}`, currentTag));
        return;
      }
    }

    if (location.pathname.includes("cover-image") && !coverImages?.length) {
      navigate(withBookTag("/dashboard?stepCode=Upload_Your_Cover_Photo", currentTag));
    }
  }, [location.pathname, !!myBooks?.length, hasMultipleBooks, !!coverImages?.length, booksLoading, hasPlacedOrder, activeBookId]);
  
  const rebuilt = REBUILT_ROUTES.includes(location.pathname) || location.pathname === "/dashboard";

  if (rebuilt) {
    return (
      <BuilderNavProvider>
        <CustomerHeader progress={location.pathname === "/finalize-order" || location.pathname === "/pdf-offer" || location.pathname.startsWith("/gift/") || (location.pathname === "/dashboard" && !currentStepCode) ? null : <BuilderProgress />} />
        {/* No min-h-screen here: body is already this cream, and a full screen below the
            header leaves spare scroll room that iOS parks the page in when the keyboard opens. */}
        <Outlet />
      </BuilderNavProvider>
    );
  }

  return (
    <BuilderNavProvider>
      <CustomerHeader />
      <section className="relative py-10 lg:py-14 2xl:py-16  bg-md-pink-light-2">
        <div className={classNames("relative w-full px-4 sm:max-w-xl md:max-w-2xl mx-auto", location.pathname === '/shopify-checkout' ? 'lg:max-w-5xl' : 'lg:max-w-[820px]')}>
          {isCheckoutPage ? (
            <div className="relative flex-shrink flex-grow">
              <Outlet />
            </div>
          ) : (
            <div className="flex items-start gap-4 3xl:gap-6">
              <div className="relative flex-shrink flex-grow">
                <Outlet />
              </div>
            </div>
          )}
        </div>
      </section>
    </BuilderNavProvider>
  );
};

export default UserLayout;
